import { observer } from 'mobx-react-lite'
import { useCreateClientModalStore } from './CreateClientModalStore'
import { themeConfig } from '@gap/shared/theme'
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import CircularProgress from "@mui/material/CircularProgress"
import Tooltip from '@mui/material/Tooltip'
import TextFieldEmail from 'reusable/mui/text-field-email/TextFieldEmail'
import Radio from '@mui/material/Radio'
import GroupsOutlined from '@mui/icons-material/GroupsOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import DiamondOutlined from '@mui/icons-material/DiamondOutlined'
import GapAlert from 'reusable/mui/alert/GapAlert'
import IconButton from '@mui/material/IconButton'

export default observer(function CreateClientModalAddManually() {
   const createClientModalStore = useCreateClientModalStore()

   return <>
      {createClientModalStore.isSyncing && <Box sx={{
         display: 'flex',
         alignItems: 'center',
         gap: 1.5,
         p: 1.5,
         mb: 2,
         borderRadius: '8px',
         backgroundColor: themeConfig.primary[100],
         border: `1px solid ${themeConfig.primary[200]}`,
      }}>
         <CircularProgress size={16} />
         <Typography variant="body2" sx={{ color: themeConfig.primary[700] }}>
            Syncing clients from XPM…
         </Typography>
      </Box>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
         <Typography variant="body1Bold" sx={{ color: themeConfig.primary[900] }}>Client details</Typography>
         {createClientModalStore.formHandler.name.bindInput(ip =>
            <TextField placeholder="Client name*" autoFocus {...ip} {...createClientModalStore.fieldProps.name} />
         )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
         <Typography variant="body1Bold" sx={{ color: themeConfig.primary[900] }}>Primary contact</Typography>
         <Box sx={{ display: 'flex', gap: 2 }}>
            {createClientModalStore.formHandler.primaryContactFirstName.bindInput(ip =>
               <TextField placeholder="First name*" {...ip} {...createClientModalStore.fieldProps.primaryContactFirstName} />
            )}
            {createClientModalStore.formHandler.primaryContactLastName.bindInput(ip =>
               <TextField placeholder="Last name*" {...ip} {...createClientModalStore.fieldProps.primaryContactLastName} />
            )}
         </Box>
         {createClientModalStore.formHandler.email.bindInput(ip =>
            <TextFieldEmail placeholder="Email address*" ip={ip} {...createClientModalStore.fieldProps.email} />
         )}
         {createClientModalStore.formHandler.primaryContactPhoneNumber.bindInput(ip =>
            <TextField placeholder="Phone number" {...ip} {...createClientModalStore.fieldProps.primaryContactPhoneNumber} />
         )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1Bold" sx={{ color: themeConfig.primary[900] }}>Visibility</Typography>
            {createClientModalStore.visibilityIsPremiumFeature && (
               <Tooltip title={createClientModalStore.visibilityTooltip}>
                  <IconButton sx={{ backgroundColor: themeConfig.system.success[300], ':hover': { backgroundColor: themeConfig.system.success[300] } }}>
                     <DiamondOutlined sx={{ fontSize: 20, color: themeConfig.primary[600] }} />
                  </IconButton>
               </Tooltip>
            )}
         </Box>
         <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>Choose who has access to this client</Typography>
         {createClientModalStore.isOnTrial && (
            <GapAlert
               severity="info"
               variant="standard"
               action={{ title: 'View plans', onClick: createClientModalStore.viewPlansHandler }}
               sx={{ mt: 1 }}
            >
               Client visibility controls are included in your trial, but are only available on select plans.
            </GapAlert>
         )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
         <VisibilityOption
            value="public"
            selected={createClientModalStore.visibility === 'public'}
            disabled={createClientModalStore.visibilityDisabled}
            disabledTooltip={createClientModalStore.visibilityTooltip}
            title={`Everyone at ${createClientModalStore.advisorOrgName}`}
            subtitle="Everyone in your organisation can access this client and everything associated with them"
            icon={<GroupsOutlined sx={{ fontSize: 20, color: themeConfig.primary[800] }} />}
            onClick={createClientModalStore.setVisibilityHandler}
         />
         <VisibilityOption
            value="private"
            selected={createClientModalStore.visibility === 'private'}
            disabled={createClientModalStore.visibilityDisabled}
            disabledTooltip={createClientModalStore.visibilityTooltip}
            title="Private"
            subtitle="Only those you explicitly invite will have access"
            icon={<LockOutlined sx={{ fontSize: 20, color: themeConfig.primary[800] }} />}
            onClick={createClientModalStore.setVisibilityHandler}
         />
      </Box>

   </>
})

interface VisibilityOptionProps {
   value: 'public' | 'private'
   selected: boolean
   disabled?: boolean
   disabledTooltip?: string
   title: string
   subtitle: string
   icon: React.ReactNode
   onClick: (event: React.MouseEvent<HTMLElement>) => void
}

const VisibilityOption = observer(function VisibilityOption(props: VisibilityOptionProps) {
   const row = <Box
      data-visibility={props.value}
      onClick={props.disabled ? undefined : props.onClick}
      sx={{
         display: 'flex',
         alignItems: 'center',
         gap: 2,
         p: 2,
         pr: 3,
         border: `1px solid ${props.selected ? themeConfig.primary[800] : themeConfig.primary[300]}`,
         borderRadius: '16px',
         cursor: props.disabled ? 'not-allowed' : 'pointer',
         opacity: props.disabled ? 0.5 : 1,
         transition: 'border-color 0.15s',
         '&:hover': {
            borderColor: props.disabled ? themeConfig.primary[300] : themeConfig.primary[800],
         },
      }}
   >
      <Radio
         checked={props.selected}
         value={props.value}
         tabIndex={-1}
         disabled={props.disabled}
         sx={{ p: 0, color: themeConfig.primary[600], '&.Mui-checked': { color: themeConfig.primary[800] } }}
      />
      <Box sx={{ flexGrow: 1 }}>
         <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>{props.title}</Typography>
         <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>{props.subtitle}</Typography>
      </Box>
      {props.icon}
   </Box>

   if (props.disabled && props.disabledTooltip) {
      return <Tooltip title={props.disabledTooltip}>{row}</Tooltip>
   }
   return row
})
